import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import Text from '../../../../components/Text';
import { styles } from '../../../../styles/themes/style';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { ICampaign } from '../../../../interfaces/models/campaign';
import { useAccountActivityRequest } from '../../../../services/apis/accountActivity/useAccountActivityRequest';
import {
  ACCOUNT_LIMIT_META,
  AccountLimitType,
  IAccountActivityStatus,
  limitTypesForStep,
} from '../../../../interfaces/models/accountActivity';
import { SIM_EVENT } from '../../hooks/useCampaignSimulation';

interface Props {
  campaign: ICampaign;
}

const LIMIT_LABELS = Object.fromEntries(
  ACCOUNT_LIMIT_META.map(m => [m.key, m.label]),
) as Record<string, string>;

/**
 * Shows when any sequence step type has hit the account-level daily limit.
 * Uses GET /user/account-activity (one call) — not extension-step-check per step.
 */
const CampaignAccountLimitBanner: React.FC<Props> = ({ campaign }) => {
  const { getAccountActivity } = useAccountActivityRequest();
  const getAccountActivityRef = useRef(getAccountActivity);
  getAccountActivityRef.current = getAccountActivity;

  const [hits, setHits] = useState<{ label: string; resetsAt?: string }[]>([]);
  const sequenceKey = (campaign.sequence ?? []).map(s => s.type).join('|');

  useEffect(() => {
    if ((campaign.executionMethod ?? 'extension') === 'proxy') return;
    if (campaign.status !== 'active' && campaign.status !== 'paused') return;

    let cancelled = false;
    let simTimer: ReturnType<typeof setTimeout> | null = null;

    const evaluate = (data: IAccountActivityStatus) => {
      const limitTypes = Array.from(new Set(
        (campaign.sequence ?? []).flatMap(s => limitTypesForStep(s.type)),
      ));
      const reached: { label: string; resetsAt?: string }[] = [];
      for (const limitType of limitTypes) {
        const cap = data.dailyLimits[limitType] ?? 0;
        const used = data.usageToday[limitType] ?? 0;
        if (cap > 0 && used >= cap) {
          reached.push({
            label: LIMIT_LABELS[limitType] ?? limitType,
            resetsAt: data.resetsAt,
          });
        }
      }
      setHits(reached);
    };

    const load = async () => {
      const data = await getAccountActivityRef.current();
      if (cancelled || !data?.featureEnabled) {
        if (!cancelled) setHits([]);
        return;
      }
      evaluate(data as IAccountActivityStatus);
    };

    void load();

    const onSim = () => {
      if (simTimer) clearTimeout(simTimer);
      simTimer = setTimeout(() => { void load(); }, 2000);
    };
    window.addEventListener(SIM_EVENT, onSim);

    return () => {
      cancelled = true;
      if (simTimer) clearTimeout(simTimer);
      window.removeEventListener(SIM_EVENT, onSim);
    };
  }, [campaign._id, campaign.status, campaign.executionMethod, sequenceKey]);

  if (!hits.length) return null;

  const resetText = hits[0]?.resetsAt
    ? moment(hits[0].resetsAt).format('MMM D, h:mm A')
    : 'the next LinkedIn day';

  return (
    <Banner>
      <Text $paragraph2>
        <strong>Daily limit reached</strong>
        {' — '}
        {hits.map(h => h.label).join(', ')}
        {'. Resets '}{resetText}.
        {' '}
        <SettingsLink to={AppRoutes.SETTINGS_ACCOUNT_ACTIVITY}>Adjust limits</SettingsLink>
      </Text>
    </Banner>
  );
};

const Banner = styled.div`
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid ${styles.colors.orange200};
  background: ${styles.colors.orange200};
`;

const SettingsLink = styled(Link)`
  color: ${styles.colors.primary600};
  font-weight: 600;
  text-decoration: underline;
`;

export default CampaignAccountLimitBanner;
